import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Paper,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  CircularProgress,
  Autocomplete,
  TextField,
  Typography,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import { BASE_URL } from "../constants";
import { toast } from "react-toastify";
import { Edit } from "@mui/icons-material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import { useNavigate } from "react-router-dom";

const getCurrentFYRange = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1;

  return month >= 4
    ? { start: `${year}-04-01`, end: `${year + 1}-03-31` }
    : { start: `${year - 1}-04-01`, end: `${year}-03-31` };
};

const Ledger = () => {
  const navigate = useNavigate();
  const fyRange = getCurrentFYRange();

  const [account, setAccount] = useState(null);
  const [accountOptions, setAccountOptions] = useState([]);

  const [fromDate, setFromDate] = useState(fyRange.start);
  const [toDate, setToDate] = useState(fyRange.end);

  const [vouchers, setVouchers] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(0);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [loading, setLoading] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);

  const [openAccountModal, setOpenAccountModal] = useState(true);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [accountInput, setAccountInput] = useState("");

  const accountInputRef = useRef(null);
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);
  const applyBtnRef = useRef(null);

  const debounceRef = useRef();

  const handleDateKey = (e, nextRef) => {
    if (e.key === "Enter") {
      e.preventDefault();
      nextRef?.current?.focus();
    }
  };

  const fetchAccounts = async (search = "") => {
    if (!search) return setAccountOptions([]);

    setAccountLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}api/user/common/accounts/search?q=${search}`,
        {
          headers: {},
        },
      );

      const data = await res.json();
      if (data.success) setAccountOptions(data.accounts);
    } catch {
      toast.error("Error fetching accounts");
    } finally {
      setAccountLoading(false);
    }
  };

  const fetchCashSaleAccounts = async () => {
    setAccountLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}api/user/common/accounts/search?cashSale=1`,
        {
          headers: {},
        },
      );

      const data = await res.json();
      if (data.success) setAccountOptions(data.accounts);
    } catch {
      toast.error("Error fetching accounts");
    } finally {
      setAccountLoading(false);
    }
  };

  // useEffect(() => {
  //   if (accountOptions.length === 0) fetchCashSaleAccounts();
  // }, [accountOptions]);

  useEffect(() => {
    fetchCashSaleAccounts();
  }, []);

  const handleAccountInputChange = (value) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchAccounts(value), 400);
  };

  const fetchVouchers = async (pageNo = 0) => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        account: account?.name || "",
        fromDate,
        toDate,
        page: pageNo,
        limit: rowsPerPage,
      });

      const res = await fetch(`${BASE_URL}api/user/common/vouchers?${query}`, {
        headers: {},
      });

      const data = await res.json();

      if (data.success) {
        setVouchers(data.vouchers);
        setTotalCount(data.totalCount);
        setOpeningBalance(data.openingBalance || 0);
        setTimeout(() => {
          accountInputRef.current?.focus();
          accountInputRef.current?.select();
        }, 0);
      } else {
        toast.error(data.message);
      }
    } catch {
      toast.error("Error fetching vouchers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    accountInputRef.current?.focus();
    if (account) fetchVouchers(page);
  }, [page, rowsPerPage]);

  useEffect(() => {
    const handleKey = (e) => {
      if (!openAccountModal) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIndex((prev) =>
          Math.min(prev + 1, accountOptions.length - 1),
        );
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIndex((prev) => Math.max(prev - 1, 0));
      }

      if (e.key === "Enter") {
        e.preventDefault();
        const selected = accountOptions[highlightIndex];
        if (selected) {
          setAccount(selected);
          setAccountInput(selected.name);
          setOpenAccountModal(false);
          setTimeout(() => {
            fromDateRef.current?.focus();
          }, 0);
        }
      }

      if (e.key === "Escape") {
        setOpenAccountModal(false);
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [openAccountModal, highlightIndex, accountOptions]);

  const editVoucher = (voucher) => {
    const voucherGroupId = voucher?.voucherGroupId;
    navigate("/dashboard/manualVouchers", { state: { voucherGroupId } });
  };

  return (
    <Box>
      {/* FILTER BAR */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          gap: 1.25,
          alignItems: "flex-end",
          flexWrap: "wrap",
          background: "#ffffff",
          border: "1px solid #e5e9ef",
          borderRadius: "14px",
          px: 2,
          py: 1.5,
          boxShadow: "0 2px 8px rgba(10,31,68,0.06)",
        }}
      >
        {/* Icon + label */}
        {/* <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
            mb: 0.25,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "8px",
              background: "linear-gradient(135deg, #2a54a6, #b93acf)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 3px 8px rgba(185,58,207,0.25)",
            }}
          >
            <FilterAltIcon sx={{ color: "#fff", fontSize: "0.95rem" }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: "0.78rem",
                fontWeight: 700,
                color: "#0a1f44",
                lineHeight: 1.2,
              }}
            >
              Ledger Filter
            </Typography>
            <Typography sx={{ fontSize: "0.6rem", color: "#94a3b8" }}>
              Press Enter to move between fields
            </Typography>
          </Box>
        </Box> */}

        {/* Vertical divider */}
        {/* <Box
          sx={{
            width: "1px",
            height: 36,
            background: "#e5e9ef",
            flexShrink: 0,
            display: { xs: "none", sm: "block" },
          }}
        /> */}

        {/* Account search */}
        <Box sx={{ minWidth: 200, flex: 1 }}>
          <Typography sx={filterLabelStyle}>Account</Typography>
          <TextField
            size="small"
            placeholder="Search account…"
            value={accountInput}
            inputRef={accountInputRef}
            onChange={(e) => {
              setOpenAccountModal(true);
              setAccountInput(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "0.9rem", color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={filterFieldStyle}
          />
        </Box>

        {/* From date */}
        <Box sx={{ minWidth: 140 }}>
          <Typography sx={filterLabelStyle}>From</Typography>
          <TextField
            type="date"
            size="small"
            value={fromDate}
            inputRef={fromDateRef}
            onKeyDown={(e) => handleDateKey(e, toDateRef)}
            inputProps={{ min: fyRange.start, max: fyRange.end }}
            onChange={(e) => setFromDate(e.target.value)}
            sx={filterFieldStyle}
          />
        </Box>

        {/* To date */}
        <Box sx={{ minWidth: 140 }}>
          <Typography sx={filterLabelStyle}>To</Typography>
          <TextField
            type="date"
            size="small"
            value={toDate}
            inputRef={toDateRef}
            onKeyDown={(e) => handleDateKey(e, applyBtnRef)}
            inputProps={{ min: fyRange.start, max: fyRange.end }}
            onChange={(e) => setToDate(e.target.value)}
            sx={filterFieldStyle}
          />
        </Box>

        {/* Apply button */}
        <Box sx={{ mb: 0.25 }}>
          <Button
            ref={applyBtnRef}
            variant="contained"
            size="small"
            startIcon={<CheckIcon sx={{ fontSize: "0.85rem !important" }} />}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.target.click();
              }
            }}
            onClick={() => {
              if (!account) return toast.error("Select account");
              if (fromDate > toDate) return toast.error("Invalid date range");
              setPage(0);
              fetchVouchers(0);
            }}
            sx={{
              textTransform: "none",
              fontSize: "0.8rem",
              fontWeight: 700,
              borderRadius: "10px",
              px: 2.5,
              py: "7px",
              background: "linear-gradient(135deg, #2a54a6, #b93acf)",
              color: "#fff",
              boxShadow: "0 4px 12px rgba(185,58,207,0.28)",
              "&:hover": {
                background: "linear-gradient(135deg, #1e3d8a, #a02db8)",
                boxShadow: "0 6px 18px rgba(185,58,207,0.4)",
              },
              transition: "all 0.25s ease",
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>

      {/* TABLE */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          border: "1px solid #e5e9ef",
          boxShadow: "0 2px 12px rgba(10,31,68,0.06)",
        }}
      >
        <TableContainer sx={{ maxHeight: "65vh" }}>
          <Table stickyHeader size="small">
            {/* ── Head ── */}
            <TableHead>
              <TableRow>
                {[
                  { label: "Date", align: "left" },
                  { label: "Narration", align: "left" },
                  { label: "Mirror Account", align: "left" },
                  { label: "Dr", align: "right" },
                  { label: "Cr", align: "right" },
                  { label: "Balance", align: "right" },
                  { label: "Action", align: "right" },
                ].map((col) => (
                  <TableCell
                    key={col.label}
                    align={col.align}
                    sx={{
                      background: "linear-gradient(135deg, #0a1f44, #1a3568)",
                      fontSize: "0.62rem",
                      fontWeight: 700,
                      color: "rgba(255,255,255,0.82)",
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                      py: "10px",
                      px: "14px",
                      borderBottom: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            {/* ── Body ── */}
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    align="center"
                    sx={{ py: 4, borderBottom: 0 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <CircularProgress size={22} sx={{ color: "#b93acf" }} />
                      <Typography
                        sx={{ fontSize: "0.75rem", color: "#94a3b8" }}
                      >
                        Loading transactions…
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {/* Opening balance row */}
                  <TableRow
                    sx={{
                      background:
                        "linear-gradient(135deg, rgba(42,84,166,0.08), rgba(185,58,207,0.05))",
                      "& .MuiTableCell-root": {
                        py: "9px",
                        px: "14px",
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        borderBottom: "1.5px solid rgba(42,84,166,0.15)",
                      },
                    }}
                  >
                    <TableCell colSpan={3}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg,#2a54a6,#b93acf)",
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: "0.78rem",
                            fontWeight: 700,
                            background:
                              "linear-gradient(135deg,#2a54a6,#b93acf)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }}
                        >
                          Opening Balance
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell align="right">
                      {openingBalance > 0 && (
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            color: "#059669",
                            fontFamily: "monospace",
                          }}
                        >
                          ₹{openingBalance.toLocaleString()}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="right">
                      {openingBalance < 0 && (
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            color: "#dc2626",
                            fontFamily: "monospace",
                          }}
                        >
                          ₹{Math.abs(openingBalance).toLocaleString()}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="right">
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          background:
                            openingBalance >= 0
                              ? "rgba(16,185,129,0.1)"
                              : "rgba(239,68,68,0.08)",
                          border: `1px solid ${openingBalance >= 0 ? "rgba(16,185,129,0.25)" : "rgba(239,68,68,0.2)"}`,
                          borderRadius: "100px",
                          px: "8px",
                          py: "2px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            color: openingBalance >= 0 ? "#059669" : "#dc2626",
                            fontFamily: "monospace",
                          }}
                        >
                          ₹{Math.abs(openingBalance).toLocaleString()}{" "}
                          {openingBalance >= 0 ? "Dr" : "Cr"}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell />
                  </TableRow>

                  {/* No data */}
                  {vouchers.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        align="center"
                        sx={{ py: 5, borderBottom: 0 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: "12px",
                              background:
                                "linear-gradient(135deg,rgba(42,84,166,0.08),rgba(185,58,207,0.06))",
                              border: "1px solid rgba(185,58,207,0.15)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <ReceiptLongIcon
                              sx={{ fontSize: "1.2rem", color: "#94a3b8" }}
                            />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "0.82rem",
                              fontWeight: 600,
                              color: "#0a1f44",
                            }}
                          >
                            No records found
                          </Typography>
                          <Typography
                            sx={{ fontSize: "0.72rem", color: "#94a3b8" }}
                          >
                            No transactions match the selected filters
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    vouchers.map((v, i) => (
                      <TableRow
                        key={i}
                        sx={{
                          background: i % 2 === 0 ? "#ffffff" : "#fafbfc",
                          transition: "background 0.15s ease",
                          "&:hover": { background: "rgba(42,84,166,0.03)" },
                          "&:last-child .MuiTableCell-root": {
                            borderBottom: 0,
                          },
                          "& .MuiTableCell-root": {
                            py: "8px",
                            px: "14px",
                            fontSize: "0.82rem",
                            color: "#374151",
                            borderBottom: "1px solid #f1f5f9",
                          },
                        }}
                      >
                        {/* Date */}
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: "0.78rem",
                              color: "#64748b",
                              fontWeight: 500,
                            }}
                          >
                            {new Date(v.createdAt).toLocaleDateString("en-GB", {
                              day: "numeric",
                              month: "short",
                              year: "2-digit",
                            })}
                          </Typography>
                        </TableCell>

                        {/* Narration */}
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: "0.82rem",
                              color: "#0a1f44",
                              fontWeight: 500,
                              maxWidth: 220,
                              // overflow: "hidden",
                              // textOverflow: "ellipsis",
                              whiteSpace: "wrap",
                            }}
                          >
                            {v.narration || (
                              <span
                                style={{
                                  color: "#cbd5e1",
                                  fontStyle: "italic",
                                }}
                              >
                                —
                              </span>
                            )}
                          </Typography>
                        </TableCell>

                        {/* Mirror account */}
                        <TableCell>
                          <Typography
                            sx={{ fontSize: "0.8rem", color: "#374151" }}
                          >
                            {v.mirrorAccount || "—"}
                          </Typography>
                        </TableCell>

                        {/* Debit */}
                        <TableCell align="right">
                          {v.debitAmount ? (
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                fontWeight: 700,
                                color: "#059669",
                                fontFamily: "monospace",
                              }}
                            >
                              ₹{Number(v.debitAmount).toLocaleString()}
                            </Typography>
                          ) : (
                            <Typography sx={{ color: "#e5e9ef" }}>—</Typography>
                          )}
                        </TableCell>

                        {/* Credit */}
                        <TableCell align="right">
                          {v.creditAmount ? (
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                fontWeight: 700,
                                color: "#dc2626",
                                fontFamily: "monospace",
                              }}
                            >
                              ₹{Number(v.creditAmount).toLocaleString()}
                            </Typography>
                          ) : (
                            <Typography sx={{ color: "#e5e9ef" }}>—</Typography>
                          )}
                        </TableCell>

                        {/* Running balance */}
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              background:
                                v.runningBalance >= 0
                                  ? "rgba(16,185,129,0.08)"
                                  : "rgba(239,68,68,0.07)",
                              border: `1px solid ${v.runningBalance >= 0 ? "rgba(16,185,129,0.2)" : "rgba(239,68,68,0.18)"}`,
                              borderRadius: "100px",
                              px: "8px",
                              py: "2px",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 700,
                                color:
                                  v.runningBalance >= 0 ? "#059669" : "#dc2626",
                                fontFamily: "monospace",
                              }}
                            >
                              ₹{Math.abs(v.runningBalance).toLocaleString()}{" "}
                              {v.runningBalance >= 0 ? "Dr" : "Cr"}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Action */}
                        <TableCell align="right">
                          {v.voucherNature === "manual" &&
                          v.referenceId === null ? (
                            <Tooltip
                              title="Edit voucher"
                              arrow
                              placement="left"
                            >
                              <IconButton
                                size="small"
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: "8px",
                                  background: "rgba(42,84,166,0.07)",
                                  border: "1px solid rgba(42,84,166,0.15)",
                                  "&:hover": {
                                    background:
                                      "linear-gradient(135deg,rgba(42,84,166,0.15),rgba(185,58,207,0.1))",
                                    borderColor: "rgba(185,58,207,0.25)",
                                  },
                                  transition: "all 0.2s",
                                }}
                                onClick={() => editVoucher(v)}
                              >
                                <Edit
                                  sx={{
                                    fontSize: "0.85rem",
                                    color: "#2a54a6",
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            ""
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ borderColor: "#f1f5f9" }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            borderTop: "none",
            background:
              "linear-gradient(135deg,rgba(42,84,166,0.03),rgba(185,58,207,0.02))",
          }}
        >
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, n) => setPage(n)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              "& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel":
                {
                  fontSize: "0.75rem",
                  color: "#64748b",
                },
              "& .MuiTablePagination-select": { fontSize: "0.75rem" },
              "& .MuiIconButton-root": { color: "#2a54a6" },
              "& .MuiIconButton-root.Mui-disabled": { color: "#cbd5e1" },
            }}
          />
        </Box>
      </Paper>

      <Dialog
        open={openAccountModal}
        onClose={() => setOpenAccountModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Title */}
            <Typography fontWeight={600} fontSize={16}>
              Search Account
            </Typography>

            {/* Date Chip */}
            <Chip
              label={
                "Upto: " +
                new Date().toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
              }
              size="small"
              sx={{
                fontWeight: 500,
                background: "#e0f2fe",
                color: "#0369a1",
              }}
            />
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Type to search..."
            value={accountInput}
            onChange={(e) => {
              const val = e.target.value;
              setAccountInput(val);
              handleAccountInputChange(val);
              setHighlightIndex(0);
            }}
            sx={{ mb: 2 }}
          />

          {accountLoading ? (
            <Box textAlign="center">
              <CircularProgress size={24} />
            </Box>
          ) : accountOptions.length === 0 ? (
            <Typography>No results found</Typography>
          ) : (
            <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
              {/* 🔥 HEADER */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 140px",
                  px: 1.5,
                  py: 1.2,
                  borderBottom: "1px solid #374151",

                  fontSize: 13,
                  fontWeight: 600,

                  background: "#111827", // 🔥 dark background
                  color: "#f9fafb", // 🔥 bright text

                  letterSpacing: 0.3,
                }}
              >
                <Typography fontSize={13} fontWeight={600}>
                  Account Name
                </Typography>

                <Typography fontSize={13} fontWeight={600} textAlign="right">
                  Balance
                </Typography>
              </Box>

              {/* 🔥 ROWS */}
              {accountOptions.map((acc, index) => (
                <Box
                  key={acc._id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 140px",
                    alignItems: "center",
                    px: 1.5,
                    py: 1.2,
                    cursor: "pointer",
                    borderBottom: "1px solid #f1f5f9",

                    background:
                      index === highlightIndex ? "#2563eb" : "transparent",
                    color: index === highlightIndex ? "#fff" : "#111",

                    "&:hover": {
                      background: "#2563eb",
                      color: "#fff",
                    },
                  }}
                >
                  {/* Account Name */}
                  <Typography fontSize={14}>{acc.name}</Typography>

                  {/* Balance */}
                  <Typography
                    fontSize={13}
                    fontWeight={500}
                    textAlign="right"
                    sx={{
                      fontFamily: "monospace",
                      color:
                        index === highlightIndex
                          ? "#fff"
                          : acc.balance >= 0
                            ? "#16a34a"
                            : "#dc2626",
                    }}
                  >
                    ₹ {Math.abs(acc.balance || 0).toLocaleString()}{" "}
                    {acc.balance >= 0 ? "Dr" : "Cr"}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Ledger;

const filterLabelStyle = {
  fontSize: "0.62rem",
  fontWeight: 600,
  color: "#64748b",
  textTransform: "uppercase",
  letterSpacing: "0.3px",
  mb: 0.5,
};

const filterFieldStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    background: "#f8fafc",
    fontSize: "0.85rem",
    "& fieldset": { borderColor: "#e5e9ef" },
    "&:hover fieldset": { borderColor: "#2a54a6" },
    "&.Mui-focused fieldset": { borderColor: "#b93acf", borderWidth: "1.5px" },
    "&.Mui-focused": { background: "#ffffff" },
  },
};
